/**
 * Индикатор текущей привязки под курсором — по нему видно, куда встанет конец стены:
 *   • угол — оранжевый квадрат с подписью «угол»: стена сойдётся с углом, контур замкнётся;
 *   • стена — оранжевое кольцо с подписью «стена»: стена встанет Т-стыком в тело стены,
 *     а рядом размеры до её концов (`DrawClearances2D`) — видно, что это не угол;
 *   • выравнивание, сетка и свободная точка — маленькое синее кольцо без подписи.
 * Если свободный конец соседней стены дотянется до новой (`stretch`), от него до угла —
 * оранжевый пунктир: видно заранее, что та стена удлинится или укоротится.
 * Раньше угол и стена выглядели одинаково, и у самого угла было непонятно, попал ли курсор
 * в угол или в стену в 10 см от него.
 */
import type { Pt } from "@/shared/lib/geometry";
import type { SnapResult } from "@/widgets/plan";

interface Props {
	snap: SnapResult;
	mmPerPx: number;
	/** Где сам курсор: подпись встаёт с другой стороны — со стороны курсора идут размеры */
	pointer?: Pt | null;
}

const ORANGE = "#f59e0b";
const BLUE = "#2f6feb";

/** Подпись у метки: что поймала привязка. */
const Tag = ({
	x,
	y,
	text,
	mmPerPx,
	toLeft,
}: {
	x: number;
	y: number;
	text: string;
	mmPerPx: number;
	/** Подпись слева от метки, а не справа */
	toLeft: boolean;
}) => {
	const px = (n: number) => n * mmPerPx;
	const fontSize = px(10);
	const width = text.length * fontSize * 0.62 + px(8);
	const height = fontSize + px(4);
	const left = toLeft ? x - px(10) - width : x + px(10);
	const top = y - px(10) - height;
	return (
		<g>
			<rect
				x={left}
				y={top}
				width={width}
				height={height}
				rx={px(3)}
				fill="#ffffff"
				stroke={ORANGE}
				strokeWidth={px(1)}
			/>
			<text
				x={left + width / 2}
				y={top + height / 2}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={fontSize}
				fontWeight={600}
				fill="#b45309"
				style={{ userSelect: "none" }}
			>
				{text}
			</text>
		</g>
	);
};

export const SnapMarker = ({ snap, mmPerPx, pointer }: Props) => {
	const px = (n: number) => n * mmPerPx;
	const { x, y } = snap.point;
	const toLeft = pointer ? pointer.x > x : false;
	if (snap.kind === "node") {
		const half = px(6);
		const stretch = snap.stretch;
		return (
			<g style={{ pointerEvents: "none" }}>
				{stretch && (
					<line
						x1={stretch.from.x}
						y1={stretch.from.y}
						x2={x}
						y2={y}
						stroke={ORANGE}
						strokeWidth={px(2)}
						strokeDasharray={`${px(5)} ${px(3)}`}
					/>
				)}
				<rect
					x={x - half}
					y={y - half}
					width={half * 2}
					height={half * 2}
					fill={ORANGE}
					fillOpacity={0.25}
					stroke={ORANGE}
					strokeWidth={px(2)}
				/>
				<Tag x={x} y={y} text="угол" mmPerPx={mmPerPx} toLeft={toLeft} />
			</g>
		);
	}
	if (snap.kind === "wall") {
		return (
			<g style={{ pointerEvents: "none" }}>
				<circle cx={x} cy={y} r={px(7)} fill="none" stroke={ORANGE} strokeWidth={px(2)} />
				<circle cx={x} cy={y} r={px(1.8)} fill={ORANGE} />
				<Tag x={x} y={y} text="стена" mmPerPx={mmPerPx} toLeft={toLeft} />
			</g>
		);
	}
	return (
		<circle
			cx={x}
			cy={y}
			r={px(4)}
			fill="none"
			stroke={BLUE}
			strokeWidth={px(1.5)}
			style={{ pointerEvents: "none" }}
		/>
	);
};
